import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDashboardStore } from '../stores/dashboardStore';
import './QA.css';

const QA: React.FC = () => {
    const { config } = useDashboardStore();
    const [activeCategory, setActiveCategory] = useState<string>('all');

    // Mock Q&A data
    const qaData = [
        {
            id: 1,
            category: 'education',
            question: 'Why did you choose product manager as a career?',
            answer: 'I chose product manager as a career for the following main reasons:\n\nFirst, I have a solid technical learning background. I systematically studied courses such as programming, user experience design, and system analysis, have a deep understanding of software product architecture, feasibility, and usability, and can communicate effectively with technical teams. At the same time, I have self-learned AI-related technical content such as AIGC and RAG, maintaining continuous attention to the underlying logic and development direction of AI products.\n\nSecond, I am good at insight into user needs. In project experiences, I was responsible for designing questionnaires, conducting user interviews, organizing insights and modeling, and am accustomed to digging real needs from user behavior and motivation rather than staying at surface pain points. I enjoy the process of communicating with users and structuring information.\n\nThird, I have data analysis and validation capabilities. Familiar with data analysis processes, able to identify problems and opportunities from data, and validate product assumptions through small-scale experiments, thereby promoting rapid product iteration and optimization.\n\nFinally, I want to create "valuable products". I hope to build products that truly help users, improve efficiency, and even improve ways of life and work. Especially today as AI applications continue to develop, I look forward to participating in building the next generation of intelligent products, making technology create value for more users.',
            tags: ['Career Choice', 'Product Thinking', 'Technical Background']
        },
        {
            id: 2,
            category: 'experience',
            question: 'What was your biggest gain from your internship?',
            answer: '✅ First, Product Practical Ability: From "Understanding Process" to "Being Able to Execute"\n\nPreviously, I only knew processes from books, but the internship allowed me to truly experience the complete cycle of products from research → solution design → review → joint debugging → launch → review, understanding the details and rhythm of product implementation.\n\n1. Designed and executed questionnaires and user interviews, putting user needs into real scenarios\n\n2. Participated in AB experiment design and analysis, experiencing data-driven decisions and rapid assumption validation\n\n3. Participated in feature design, requirements documentation writing, and aligning solutions with R&D\n\n4. Followed up on development, joint debugging and launch, and reviewed launch effects and feedback\n\nThese experiences transformed me from knowing to doing - clearer product decision logic, and the ability to independently push small modules to launch.\n\n✅ Second, Team Collaboration and Work Methods: More Professional, More Efficient\n\nBeyond methodology, another core growth I gained was how to collaborate and communicate more effectively with colleagues.\n\n1. Developed a habit of work documentation: Requirement changes, design basis, data conclusions are all documented, reducing communication losses\n\n2. Learned cross-team alignment: Prepare information in advance, clarify owner and timeline, actively sync at key nodes\n\n3. Developed awareness of high-quality problem handling: When encountering problems, not just "reporting problems", but communicating with background, alternative solutions and evaluation\n\n4. Structured expression and review culture: Record ideas, experimental conclusions, and paths clearly, facilitating iteration\n\nThese habits upgraded my internship from "executing tasks" to "participating in project advancement".',
            tags: ['Internship Experience', 'Skill Improvement', 'Product Thinking']
        },
        {
            id: 5,
            category: 'future',
            question: 'What are your plans for future product manager career development?',
            answer: `
I hope to deeply cultivate in the AI product direction in the long term. AI is the future general-purpose technology platform (General Purpose Tech). It is not a function or a type of product, but will continuously reshape the way we acquire knowledge, complete tasks, and interact with systems.

Current AI is experiencing three clear turning points:

**1. From Capability Breakthrough → Scenario Landing**

Large model capabilities continue to evolve, but the real difficulty has shifted: how to embed AI into specific businesses, making it "usable, reusable system capabilities" rather than a Demo or short-term tool.

**2. From Tool Form → Agent Interaction**

Current product forms are still mainly tools and assistants, but I am more concerned with intelligence at the levels of task execution, intent understanding, and strategic planning. Directions such as Agent, Workflow, and Memory are defining the next generation of human-computer interaction paradigms.

**3. From "Technology Display" → "Value Creation"**

Many AI products remain at the "model packaging + application shell" stage. What's really difficult is:

- Finding continuous use scenarios

- Establishing feedback loops and self-learning mechanisms

- Using AI to重构 experience, processes and efficiency, rather than labeling old processes with AI

I hope to become an AI product person who understands users, scenarios, and technical constraints, not only able to deeply cooperate with technical teams, but also able to judge where AI can truly bring value and how to transform technology into capabilities and experiences.

**✨ What I want to do is not "adding AI to products", but "redefining products with AI"**

In the next few years, I hope to explore directions including:

- Multi-turn task completion AI (Agent Systems)

- Industry vertical agents (Vertical AI): Education, knowledge management, productivity tools, information services

- AI experience design and behavior feedback mechanisms

AI products are not a position, but an era transformation. I want to build things that truly change users' work and lifestyle within it.
            `,
            tags: ['Career Planning', 'Development Goals', 'Learning Plan']
        }
    ];

    const categories = [
        { id: 'all', name: 'All', count: qaData.length },
        { id: 'education', name: 'Education Background', count: qaData.filter(q => q.category === 'education').length },
        { id: 'experience', name: 'Internship Experience', count: qaData.filter(q => q.category === 'experience').length },
        { id: 'projects', name: 'Project Experience', count: qaData.filter(q => q.category === 'projects').length },
        { id: 'skills', name: 'Skills & Abilities', count: qaData.filter(q => q.category === 'skills').length },
        { id: 'future', name: 'Career Planning', count: qaData.filter(q => q.category === 'future').length }
    ];

    const filteredQA = activeCategory === 'all'
        ? qaData
        : qaData.filter(q => q.category === activeCategory);

    return (
        <div className="qa-page">
            <div className="container">
                <div className="page-header">
                    <h1>Q&A Section</h1>
                    <p>Here I answer questions about my education background, internship experience, project experience and career planning</p>
                </div>

                <div className="qa-content">
                    <div className="category-sidebar">
                        <h3>Question Categories</h3>
                        <ul className="category-list">
                            {categories.map((category) => (
                                <li key={category.id}>
                                    <button
                                        className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
                                        onClick={() => setActiveCategory(category.id)}
                                    >
                                        {category.name}
                                        <span className="count">({category.count})</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="qa-list">
                        {filteredQA.map((qa) => (
                            <div key={qa.id} className="qa-item">
                                <div className="question">
                                    <h3>{qa.question}</h3>
                                    <div className="tags">
                                        {qa.tags.map((tag, index) => (
                                            <span key={index} className="tag">{tag}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="answer">
                                    <ReactMarkdown>{qa.answer}</ReactMarkdown>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="contact-section">
                    <h2>Have More Questions?</h2>
                    <p>If you have any other questions, please feel free to contact me through the following methods:</p>
                    <div className="contact-methods">
                        <a href={`mailto:${config.footer.contact.email}`} className="contact-method">
                            <span className="icon">📧</span>
                            <span>Send Email</span>
                        </a>
                        {config.footer.contact.linkedin && (
                            <a href={config.footer.contact.linkedin} target="_blank" rel="noopener noreferrer" className="contact-method">
                                <span className="icon">💼</span>
                                <span>LinkedIn</span>
                            </a>
                        )}
                        {config.footer.contact.github && (
                            <a href={config.footer.contact.github} target="_blank" rel="noopener noreferrer" className="contact-method">
                                <span className="icon">💻</span>
                                <span>GitHub</span>
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QA;
